import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { deleteBusiness, fetchBusinesses } from "../../../store/business/businessReducer";
import Spinner from "../../../components/Spinner/Index";
import googleLogo from "../../../assets/images/google.svg";
import Swal from "sweetalert2";

const Business = () => {
    const dispatch = useDispatch();
    const { businesses, isLoading, error } = useSelector((state) => state.business);
    const { user } = useSelector((state) => state.auth);

    useEffect(() => {
        dispatch(fetchBusinesses());
    }, []);

    const handleDelete = (businessId: string) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#22c55e",
            confirmButtonText: "Yes, delete it!",
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteBusiness(businessId) as any).then(() => {
                    Swal.fire("Deleted!", "Your business has been deleted.", "success");
                });
            }
        });
    };

    if (isLoading) {
        return <Spinner />;
    }

    if (error) {
        return <p>Error: {error}</p>;
    }

    return (
        <div className="p-6">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
                <h1 className="text-2xl font-bold text-center sm:text-left flex-1">
                    Your Business Listings
                </h1>

                {/* Button Container */}
                <div className="w-full sm:w-auto mt-4 sm:mt-0 flex flex-col sm:flex-row gap-2 items-center sm:items-end justify-center sm:justify-start">
                    <Link
                        to="create"
                        className="w-full sm:w-auto px-4 py-3 bg-green-500 text-white text-sm font-medium rounded hover:bg-green-600 text-center"
                    >
                        Add Your Business
                    </Link>
                </div>
            </div>




            {/* Business Cards */}
            <div className="bg-white shadow rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Business
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Owner
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Category
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Address
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {businesses.map((business, index) => (
                            <tr key={index} >
                                {/* Business Name and Thumbnail */}
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="h-10 w-10 flex-shrink-0">
                                            <img
                                                className="h-10 w-10 rounded-full"
                                                src={business.mediaInformation.uploadedImages[0] || "https://placehold.co/50x50"}
                                                alt="Business Thumbnail"
                                            />
                                        </div>
                                        <div className="ml-4">
                                            <div className="text-sm font-medium text-gray-900">
                                                {business.businessInformation.businessName || "Unnamed Business"}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {business.businessInformation.businessEmail || "N/A"}
                                            </div>
                                        </div>
                                    </div>
                                </td>

                                {/* Owner */}
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                        {business.businessInformation.ownerName || "N/A"}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        {business.businessInformation.phone || "N/A"}
                                    </div>
                                </td>

                                {/* Category */}
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                        {business.categoriesInformation.businessCategory || "Not Specified"}
                                    </div>
                                    {business.categoriesInformation.subcategory && (
                                        <div className="text-sm text-gray-500">
                                            Subcategory: {business.categoriesInformation.subcategory}
                                        </div>
                                    )}
                                </td>

                                {/* Address */}
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                        {[
                                            business.businessInformation.address.line1,
                                            business.businessInformation.address.city,
                                            business.businessInformation.address.state,
                                            business.businessInformation.address.zipCode,
                                        ]
                                            .filter(Boolean)
                                            .join(", ") || "No address provided"}
                                    </div>
                                </td>

                                {/* Actions */}
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <Link
                                        className="text-green-600 hover:text-green-900 mr-4"
                                        to={business._id}
                                    >
                                        View
                                    </Link>
                                    <button
                                        className="text-red-600 hover:text-red-900"
                                        onClick={() => handleDelete(business._id)}
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Business;
